import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { mockData } from '../utils/mock';
import { useLanguage } from '../context/LanguageContext';
import { Reveal, Eyebrow } from './decor';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const buildImageUrl = (img) => {
  if (!img) return '';
  return img.startsWith('http') ? img : `${BACKEND}${img}`;
};

const Trending = () => {
  const { t, language } = useLanguage();
  // Fallback shows only images (no text overlay) since default images have baked-in text
  const [trending, setTrending] = useState(mockData.trending.map((it) => ({ id: it.id, image: it.image, alt: it.alt })));
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef(null);
  const touchStart = useRef(null);
  const touchEnd = useRef(null);

  const loc = (v) => (typeof v === 'object' && v !== null ? v[language] || v.es : v);

  useEffect(() => {
    let active = true;
    fetch(`${BACKEND}/api/trending`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (active && d && Array.isArray(d.items) && d.items.length > 0) {
          setTrending(d.items.map((it) => ({ id: it.id, image: buildImageUrl(it.image), alt: it.alt, tag: it.tag, title: it.title, subtitle: it.subtitle })));
          setCurrent(0);
        }
      })
      .catch(() => { /* keep mock fallback */ });
    return () => { active = false; };
  }, []);

  const next = useCallback(() => setCurrent((p) => (p + 1) % trending.length), [trending.length]);
  const prev = () => setCurrent((p) => (p - 1 + trending.length) % trending.length);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    intervalRef.current = setInterval(next, 5000);
    return () => clearInterval(intervalRef.current);
  }, [next]);

  const handleTouchStart = (e) => {
    touchEnd.current = null;
    touchStart.current = e.targetTouches[0].clientX;
  };
  const handleTouchMove = (e) => {
    touchEnd.current = e.targetTouches[0].clientX;
  };
  const handleTouchEnd = () => {
    if (!touchStart.current || !touchEnd.current) return;
    const distance = touchStart.current - touchEnd.current;
    if (distance > 50) next();
    else if (distance < -50) prev();
  };

  const navBtnStyle = {
    width: '44px',
    height: '44px',
    border: '1px solid rgba(201,168,76,0.18)',
    color: '#C9A84C',
    transition: 'background-color 0.2s ease, color 0.2s ease'
  };

  return (
    <section id="trending" style={{ background: '#0A0A0A', padding: '100px 0' }} data-testid="trending-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-[60px]">
        <div className="grid lg:grid-cols-3 gap-12 items-start">
          {/* Left text */}
          <Reveal className="lg:col-span-1 lg:sticky lg:top-28">
            <Eyebrow>{t('sections.trendingEyebrow')}</Eyebrow>
            <h2 className="font-display mt-5" style={{ fontSize: 'clamp(30px, 4vw, 46px)', fontWeight: 700, color: '#F5F5F0', lineHeight: 1.1 }}>
              {t('sections.trendingTitlePre')}
              <span style={{ fontStyle: 'italic', color: '#C9A84C' }}>{t('sections.trendingTitleHighlight')}</span>
            </h2>
            <p className="font-body mt-5" style={{ fontSize: '15px', fontWeight: 300, color: '#C2C1B6', lineHeight: 1.8, maxWidth: '360px' }}>
              {t('sections.trendingDesc')}
            </p>

            <div className="flex items-center mt-8" style={{ gap: '12px' }}>
              <button
                onClick={prev}
                className="flex items-center justify-center"
                style={navBtnStyle}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#C9A84C'; e.currentTarget.style.color = '#0A0A0A'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#C9A84C'; }}
                data-testid="trending-prev"
                aria-label="Previous"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                className="flex items-center justify-center"
                style={navBtnStyle}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#C9A84C'; e.currentTarget.style.color = '#0A0A0A'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#C9A84C'; }}
                data-testid="trending-next"
                aria-label="Next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </Reveal>

          {/* Right slider */}
          <Reveal delay={0.15} className="lg:col-span-2">
            <div
              className="relative overflow-hidden"
              style={{ border: '1px solid rgba(201,168,76,0.18)' }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              data-testid="trending-slider"
            >
              <div className="relative" style={{ aspectRatio: '3 / 2' }}>
                {trending.map((item, i) => {
                  const tag = loc(item.tag);
                  const title = loc(item.title);
                  const subtitle = loc(item.subtitle);
                  const hasOverlay = tag || title || subtitle;
                  return (
                    <div
                      key={item.id}
                      className="absolute inset-0 transition-opacity duration-700"
                      style={{ opacity: i === current ? 1 : 0, pointerEvents: i === current ? 'auto' : 'none' }}
                    >
                      <img src={item.image} alt={loc(item.alt)} className="w-full h-full object-cover" loading={i === 0 ? 'eager' : 'lazy'} />
                      {hasOverlay && (
                        <div
                          className="absolute inset-0 flex flex-col justify-end"
                          style={{
                            background: 'linear-gradient(to top, rgba(10,10,10,0.85) 0%, rgba(10,10,10,0.35) 40%, rgba(10,10,10,0) 70%)',
                            padding: 'clamp(20px, 4vw, 44px)',
                          }}
                          data-testid={`trending-overlay-${i}`}
                        >
                          {tag && (
                            <span
                              className="font-bebas"
                              style={{ display: 'inline-block', alignSelf: 'flex-start', color: '#0A0A0A', background: '#C9A84C', padding: '4px 12px', fontSize: 'clamp(11px, 1.4vw, 13px)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}
                            >
                              {tag}
                            </span>
                          )}
                          {title && (
                            <h3 className="font-display" style={{ color: '#F5F5F0', fontSize: 'clamp(24px, 3.6vw, 44px)', fontWeight: 700, lineHeight: 1.05, textShadow: '0 2px 18px rgba(0,0,0,0.5)' }}>
                              {title}
                            </h3>
                          )}
                          {subtitle && (
                            <p className="font-body" style={{ color: '#E8C96A', fontSize: 'clamp(13px, 1.7vw, 17px)', marginTop: '8px', fontWeight: 300, textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}>
                              {subtitle}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Rectangle dots */}
            <div className="flex mt-6" style={{ gap: '10px' }}>
              {trending.map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => setCurrent(i)}
                  style={{
                    width: '28px',
                    height: '2px',
                    background: i === current ? '#C9A84C' : '#C2C1B6',
                    transition: 'background-color 0.2s ease'
                  }}
                  aria-label={`Slide ${i + 1}`}
                  data-testid={`trending-dot-${i}`}
                />
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Trending;
